import { useEffect, useMemo, useState } from 'react'
import { GameCardContainer } from 'src/components/GameCard'
import { useGetPlayableGames, useGetGameConfig } from 'src/store/game'
import type { MarbleGame } from 'src/types/domain/Game'
import { useGetHoveredGameId, useUIActions } from 'src/store/ui'
import { usePreloadGameImages } from 'src/hooks/usePreloadGameImages'
import AngleLeftIcon from 'src/app/assets/icons/ico-angle-left.svg'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import { TrackHighlight } from '../TrackHighlight'
import { DynamicTrackBackground } from '../DynamicTrackBackground'
import { ShowGamesTitle } from './components/ShowGamesTitle'
import { useGameRowScroll } from './useGameRowScroll'

type Props = Readonly<{
  isTracksPanel?: boolean
  onTrackClick: (gameId: string) => void
  currentGameId?: string
}>

export function ShowGames({ onTrackClick, currentGameId, isTracksPanel }: Props) {
  const uiActions = useUIActions()
  const games = useGetPlayableGames()
  const hoveredGameId = useGetHoveredGameId()
  const { lobbyDisabled } = useGetGameConfig()
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null)
  // Depend on the count (stable primitive), not the `games` array — that selector
  // returns a fresh array every render, which would re-run the effect each render.
  const { scrollRef, canScrollLeft, canScrollRight, scrollLeftBy, scrollRightBy } =
    useGameRowScroll([games.length])

  usePreloadGameImages(games)

  const hoveredGame: MarbleGame | null = useMemo(() => {
    return games.find((g) => g.id === hoveredGameId) || null
  }, [games, hoveredGameId])

  useEffect(() => {
    if (!hoveredGameId && games.length > 0 && games[0]) {
      const defaultId = currentGameId ?? games[0].id
      uiActions.setHoveredGameId(defaultId)
    }
  }, [games, hoveredGameId, currentGameId, uiActions])

  return (
    <>
      <TrackHighlight
        currentGameId={currentGameId}
        hoveredGame={hoveredGame}
        onTrackClick={() => {
          if (hoveredGame) {
            setLoadingTrackId(hoveredGame.id)
            onTrackClick(hoveredGame.id)
          }
        }}
      />

      {!isTracksPanel && <DynamicTrackBackground hoverTrack={hoveredGame?.track || null} />}

      {!lobbyDisabled ? (
        <>
          <ShowGamesTitle
            actions={
              <>
                <button
                  aria-label="Scroll games left"
                  className="game-row-nav"
                  disabled={!canScrollLeft}
                  onClick={scrollLeftBy}
                  type="button"
                >
                  <AngleLeftIcon />
                </button>
                <button
                  aria-label="Scroll games right"
                  className="game-row-nav"
                  disabled={!canScrollRight}
                  onClick={scrollRightBy}
                  type="button"
                >
                  <AngleRightIcon />
                </button>
              </>
            }
          />

          <div className="game-lobby-races" ref={scrollRef}>
            {games.map((game) => (
              <GameCardContainer
                game={game}
                isCurrentGame={game.id === currentGameId}
                isLoading={loadingTrackId === game.id}
                key={game.id}
                onTrackClick={() => {
                  setLoadingTrackId(game.id)
                  onTrackClick(game.id)
                }}
              />
            ))}
          </div>
        </>
      ) : null}
    </>
  )
}
